import { useMemo, useState } from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Pie,
  PieChart,
  ResponsiveContainer,
  Scatter,
  ScatterChart,
  Tooltip,
  XAxis,
  YAxis,
  ZAxis,
} from "recharts";
import {
  HYPOTHESES_REGISTRY_PART_1,
} from "./hypothesesRegistryPart1";
import {
  HYPOTHESES_REGISTRY_PART_2,
} from "./hypothesesRegistryPart2";
import { HEADLINE_FINDINGS_RAW } from "./hypothesesHeadlineData";

const STATUS = {
  confirmed_after_fdr: { label: "Подтверждены после FDR", color: "#636efa" },
  raw_only_not_fdr: { label: "Только сырая значимость", color: "#00cc96" },
  not_confirmed: { label: "Не подтверждены", color: "#ef553b" },
};

const OUTCOME_OPTIONS = [
  "ДТП с детьми",
  "ДТП с погибшими",
  "ДТП с пострадавшими",
  "Летальный исход среди ДТП с погибшими или пострадавшими",
  "Наезд на пешехода",
];

function expandRow(row) {
  return {
    id: row.i,
    family: row.a,
    factor: row.f,
    outcome: row.u,
    status: row.s,
    fdr: row.p,
    oddsRatio: row.o,
    oddsLow: row.l,
    oddsHigh: row.h,
    riskDifference: row.d,
    exposedRate: row.e,
    unexposedRate: row.n,
  };
}

const HYPOTHESES_REGISTRY = [...HYPOTHESES_REGISTRY_PART_1, ...HYPOTHESES_REGISTRY_PART_2].map(expandRow);
const HEADLINE_FINDINGS = HEADLINE_FINDINGS_RAW.map(expandRow);

const FAMILY_STATUS_ROWS = [
  { label: "Бинарные непогодные факторы", confirmed: 13, raw: 0, notConfirmed: 2 },
  { label: "Бинарные погодные факторы", confirmed: 30, raw: 10, notConfirmed: 45 },
  { label: "Глобальные категориальные связи", confirmed: 27, raw: 0, notConfirmed: 13 },
  { label: "Категория против остальных", confirmed: 117, raw: 11, notConfirmed: 92 },
  { label: "Числовые погодные признаки", confirmed: 4, raw: 3, notConfirmed: 31 },
  { label: "Погода внутри видов ДТП", confirmed: 1, raw: 5, notConfirmed: 22 },
];

const OUTCOME_STATUS_ROWS = [
  { label: "ДТП с детьми", confirmed: 52, raw: 2, notConfirmed: 31 },
  { label: "ДТП с погибшими", confirmed: 34, raw: 15, notConfirmed: 77 },
  { label: "ДТП с пострадавшими", confirmed: 9, raw: 2, notConfirmed: 21 },
  { label: "Летальный исход среди ДТП с погибшими или пострадавшими", confirmed: 33, raw: 10, notConfirmed: 55 },
  { label: "Наезд на пешехода", confirmed: 64, raw: 0, notConfirmed: 21 },
];

const numberFormat = new Intl.NumberFormat("ru-RU");

function ReportCard({ title, children, className = "" }) {
  return (
    <section className={`statistics-report-card ${className}`.trim()}>
      {title ? <h3>{title}</h3> : null}
      {children}
    </section>
  );
}

function valueOrDash(value, digits = 3) {
  return Number.isFinite(value) ? Number(value).toFixed(digits) : "—";
}

function scientific(value) {
  return Number.isFinite(value) ? Number(value).toExponential(2).replace("e-", "e−") : "—";
}

function StatusTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="statistics-report-chart-tooltip">
      <strong>{label}</strong>
      {payload.filter((item) => item.value).map((item) => (
        <div key={item.dataKey}><span style={{ background: item.color }} />{item.name}: {numberFormat.format(item.value)}</div>
      ))}
    </div>
  );
}

function StatusStackedChart({ data, height = 330 }) {
  return (
    <ResponsiveContainer width="100%" height={height}>
      <BarChart data={data} margin={{ top: 16, right: 18, bottom: 52, left: 4 }}>
        <CartesianGrid stroke="#e1e9e6" strokeDasharray="3 3" />
        <XAxis dataKey="label" tick={{ fontSize: 10, angle: -25, textAnchor: "end" }} interval={0} />
        <YAxis tick={{ fontSize: 11 }} label={{ value: "Число тестов", angle: -90, position: "insideLeft", offset: 0, fontSize: 11 }} />
        <Tooltip content={<StatusTooltip />} />
        <Legend verticalAlign="top" height={28} wrapperStyle={{ fontSize: 10 }} />
        <Bar dataKey="confirmed" name={STATUS.confirmed_after_fdr.label} stackId="status" fill={STATUS.confirmed_after_fdr.color} />
        <Bar dataKey="raw" name={STATUS.raw_only_not_fdr.label} stackId="status" fill={STATUS.raw_only_not_fdr.color} />
        <Bar dataKey="notConfirmed" name={STATUS.not_confirmed.label} stackId="status" fill={STATUS.not_confirmed.color} />
      </BarChart>
    </ResponsiveContainer>
  );
}

function StatusPie() {
  const data = [
    { label: STATUS.confirmed_after_fdr.label, value: 192, color: STATUS.confirmed_after_fdr.color },
    { label: STATUS.raw_only_not_fdr.label, value: 29, color: STATUS.raw_only_not_fdr.color },
    { label: STATUS.not_confirmed.label, value: 205, color: STATUS.not_confirmed.color },
  ];
  const label = ({ cx, cy, midAngle, innerRadius, outerRadius, payload }) => {
    const angle = (-midAngle * Math.PI) / 180;
    const radius = innerRadius + (outerRadius - innerRadius) * 0.54;
    const x = cx + radius * Math.cos(angle);
    const y = cy + radius * Math.sin(angle);
    const percent = ((payload.value / 426) * 100).toFixed(2);
    return <text x={x} y={y - 5} textAnchor="middle" fill="#fff" fontSize="10"><tspan x={x}>{payload.label}</tspan><tspan x={x} dy="12">{payload.value}</tspan><tspan x={x} dy="12">{percent}%</tspan></text>;
  };
  return (
    <ResponsiveContainer width="100%" height={350}>
      <PieChart>
        <Legend verticalAlign="top" height={38} wrapperStyle={{ fontSize: 10 }} />
        <Pie data={data} dataKey="value" nameKey="label" innerRadius="39%" outerRadius="72%" cx="50%" cy="54%" labelLine={false} label={label}>
          {data.map((row) => <Cell key={row.label} fill={row.color} />)}
        </Pie>
      </PieChart>
    </ResponsiveContainer>
  );
}

function EffectTooltip({ active, payload }) {
  if (!active || !payload?.length) return null;
  const row = payload[0].payload;
  return (
    <div className="statistics-report-chart-tooltip">
      <strong>{row.factor}</strong>
      <div>OR: {valueOrDash(row.oddsRatio)}</div>
      <div>FDR p: {scientific(row.fdr)}</div>
      <div>Статус: {STATUS[row.status]?.label || "—"}</div>
    </div>
  );
}

function EffectScatter({ rows }) {
  const data = rows
    .filter((row) => Number.isFinite(row.oddsRatio) && row.oddsRatio > 0 && Number.isFinite(row.fdr) && row.fdr > 0)
    .map((row) => ({ ...row, significance: -Math.log10(row.fdr) }));
  return (
    <ResponsiveContainer width="100%" height={500}>
      <ScatterChart margin={{ top: 14, right: 22, bottom: 28, left: 28 }}>
        <CartesianGrid stroke="#e1e9e6" strokeDasharray="3 3" />
        <XAxis type="number" dataKey="oddsRatio" scale="log" domain={[0.000001, 10000000000]} tick={{ fontSize: 10 }} label={{ value: "Odds ratio, логарифмическая шкала", position: "bottom", offset: 9, fontSize: 11 }} />
        <YAxis type="number" dataKey="significance" tick={{ fontSize: 10 }} label={{ value: "−log10(FDR p-value)", angle: -90, position: "insideLeft", offset: -5, fontSize: 11 }} />
        <ZAxis range={[18, 18]} />
        <Tooltip content={<EffectTooltip />} />
        {Object.entries(STATUS).map(([key, config]) => <Scatter key={key} name={config.label} data={data.filter((row) => row.status === key)} fill={config.color} />)}
      </ScatterChart>
    </ResponsiveContainer>
  );
}

function ForestPlot({ rows }) {
  const plotted = rows.filter((row) => Number.isFinite(row.oddsRatio) && Number.isFinite(row.oddsLow) && Number.isFinite(row.oddsHigh));
  const min = 0.1;
  const max = 5;
  const toPercent = (value) => ((Math.log10(value) - Math.log10(min)) / (Math.log10(max) - Math.log10(min))) * 100;
  return (
    <div className="statistics-hypotheses-forest" role="img" aria-label="Headline-выводы: odds ratio и 95-процентные доверительные интервалы">
      <div className="statistics-hypotheses-forest-axis">
        <span aria-hidden="true" />
        <div className="statistics-hypotheses-forest-axis-scale">
          <span className="statistics-hypotheses-forest-axis-min">0.1</span>
          <span style={{ left: `${toPercent(1)}%` }}>1</span>
          <span className="statistics-hypotheses-forest-axis-max">5</span>
        </div>
      </div>
      {plotted.map((row) => (
        <div className="statistics-hypotheses-forest-row" key={row.id}>
          <span>{row.factor}</span>
          <div className="statistics-hypotheses-forest-scale">
            <i className="statistics-hypotheses-forest-reference" />
            <i className="statistics-hypotheses-forest-interval" style={{ left: `${toPercent(Math.max(min, row.oddsLow))}%`, width: `${Math.max(0, toPercent(Math.min(max, row.oddsHigh)) - toPercent(Math.max(min, row.oddsLow)))}%` }} />
            <b style={{ left: `${toPercent(Math.min(max, Math.max(min, row.oddsRatio)))}%` }} />
          </div>
        </div>
      ))}
      <p>Odds ratio, логарифмическая шкала; пунктир соответствует OR = 1.</p>
    </div>
  );
}

function RegistryTable({ rows, headline = false }) {
  return (
    <div className="statistics-hypotheses-table-scroll">
      <table className="statistics-report-table statistics-hypotheses-registry-table">
        <thead>
          <tr>
            <th>Фактор</th><th>Исход</th>
            {headline ? <><th>Доля при факторе</th><th>Доля без фактора</th><th>OR [95% ДИ]</th></> : <><th>Статус</th><th>FDR p</th><th>OR</th><th>Разность рисков, п.п.</th></>}
            <th>Вывод</th>
          </tr>
        </thead>
        <tbody>{rows.map((row) => (
          <tr key={row.id}>
            <th>{row.factor}</th><td>{row.outcome}</td>
            {headline ? <>
              <td>{valueOrDash(row.exposedRate, 2)}%</td><td>{valueOrDash(row.unexposedRate, 2)}%</td>
              <td>{valueOrDash(row.oddsRatio)} [{valueOrDash(row.oddsLow)}; {valueOrDash(row.oddsHigh)}]</td>
            </> : <>
              <td>{STATUS[row.status]?.label || "—"}</td><td>{scientific(row.fdr)}</td><td>{valueOrDash(row.oddsRatio)}</td>
              <td>{Number.isFinite(row.riskDifference) ? `${row.riskDifference >= 0 ? "+" : ""}${row.riskDifference.toFixed(3)}` : "—"}</td>
            </>}
            <td>{row.finding || (Number.isFinite(row.exposedRate) && Number.isFinite(row.unexposedRate) ? `При факторе «${row.factor}» доля исхода составляет ${row.exposedRate.toFixed(2)}% против ${row.unexposedRate.toFixed(2)}%.` : `Результат для фактора «${row.factor}» в исходе «${row.outcome}».`)}</td>
          </tr>
        ))}</tbody>
      </table>
    </div>
  );
}

export default function HypothesesReportSection() {
  const [outcome, setOutcome] = useState("");
  const [status, setStatus] = useState("");
  const registryRows = useMemo(() => HYPOTHESES_REGISTRY
    .filter((row) => (!outcome || row.outcome === outcome) && (!status || row.status === status))
    .slice(0, 180), [outcome, status]);
  const weatherSummary = [
    { label: "Подтверждено после FDR", value: 8 },
    { label: "Не подтверждено", value: 57 },
    { label: "Только сырая значимость", value: 13 },
  ];

  return (
    <div className="statistics-hypotheses-report">
      <section className="statistics-hypotheses-hero">
        <h2>Статистические гипотезы: подтверждённые и неподтверждённые связи</h2>
        <p>Авторитетным источником вкладки является реестр из 426 выполненных тестов. Из них 221 были значимы без поправки, а 192 сохранили значимость после Benjamini–Hochberg FDR. Дополнительно 32 теста не выполнялись из-за недостаточного размера групп или отсутствия вариации.</p>
      </section>

      <section className="statistics-hypotheses-filterbar" aria-label="Фильтры реестра гипотез">
        <label>Исход для таблицы<select value={outcome} onChange={(event) => setOutcome(event.target.value)}><option value="">Все исходы</option>{OUTCOME_OPTIONS.map((item) => <option key={item} value={item}>{item}</option>)}</select></label>
        <label>Статус результата<select value={status} onChange={(event) => setStatus(event.target.value)}><option value="">Все</option>{Object.entries(STATUS).map(([key, item]) => <option key={key} value={key}>{item.label}</option>)}</select></label>
      </section>

      <div className="statistics-report-kpi-grid statistics-hypotheses-kpi-grid">
        <article className="statistics-report-kpi"><span>Выполнено тестов</span><strong>426</strong><small>Не 427: используется исходный hypothesis_registry</small></article>
        <article className="statistics-report-kpi"><span>Значимы без поправки</span><strong>221</strong><small>p &lt; 0,05</small></article>
        <article className="statistics-report-kpi"><span>Значимы после FDR</span><strong>192</strong><small>45.1% выполненных тестов</small></article>
        <article className="statistics-report-kpi"><span>Для дашборда</span><strong>125</strong><small>Содержательно пригодные выводы</small></article>
        <article className="statistics-report-kpi"><span>Headline-выводы</span><strong>30</strong><small>Приоритетные результаты</small></article>
      </div>

      <div className="statistics-report-section-heading"><h3>Общая структура результатов</h3></div>
      <div className="statistics-hypotheses-overview-grid">
        <ReportCard title="Статус подтверждённости всех выполненных тестов"><StatusPie /></ReportCard>
        <ReportCard title="Результаты тестов по семействам"><StatusStackedChart data={FAMILY_STATUS_ROWS} /></ReportCard>
        <ReportCard title="Результаты по анализируемым исходам"><StatusStackedChart data={OUTCOME_STATUS_ROWS} /></ReportCard>
      </div>

      <div className="statistics-report-section-heading"><h3>Сила и направление ассоциаций</h3></div>
      <div className="statistics-report-chart-grid">
        <ReportCard title="Карта эффектов: odds ratio и статистическая значимость"><EffectScatter rows={HYPOTHESES_REGISTRY} /></ReportCard>
        <ReportCard title="Headline-выводы: odds ratio и 95%-й доверительный интервал"><ForestPlot rows={HEADLINE_FINDINGS} /></ReportCard>
      </div>

      <div className="statistics-report-section-heading"><h3>Погодные гипотезы о летальном исходе</h3></div>
      <div className="statistics-report-chart-grid">
        <ReportCard title="Погодные тесты для летального исхода">
          <ResponsiveContainer width="100%" height={330}><BarChart data={weatherSummary} margin={{ top: 16, right: 18, bottom: 32, left: 4 }}><CartesianGrid stroke="#e1e9e6" strokeDasharray="3 3" /><XAxis dataKey="label" tick={{ fontSize: 10 }} /><YAxis tick={{ fontSize: 11 }} label={{ value: "Число тестов", angle: -90, position: "insideLeft", offset: 0, fontSize: 11 }} /><Tooltip /><Bar dataKey="value" name="Тесты" fill="#636efa" /></BarChart></ResponsiveContainer>
        </ReportCard>
        <ReportCard title="Содержательный итог" className="statistics-report-findings-card">
          <p>Для погодных тестов летального исхода большинство связей не подтвердилось после FDR. Высокая влажность дала небольшой положительный сигнал, тогда как осадки, дождь и снег не сформировали устойчивого общего вывода. Отрицательная ассоциация влажного грунта не интерпретируется как защитный причинный эффект.</p>
          <aside>Статистическая значимость и практическая значимость различаются. Для крупных выборок малый эффект может иметь очень малый p-value.</aside>
        </ReportCard>
      </div>

      <div className="statistics-report-section-heading"><h3>Фильтруемый реестр результатов</h3><p>Показывается до 180 строк из выбранного статического среза.</p></div>
      <RegistryTable rows={registryRows} />

      <div className="statistics-report-section-heading"><h3>30 приоритетных headline-выводов</h3></div>
      <RegistryTable rows={HEADLINE_FINDINGS} headline />

      <div className="statistics-report-section-heading"><h3>Пропущенные тесты</h3></div>
      <section className="statistics-hypotheses-skipped"><p><strong>32</strong> теста не выполнялись. Основные причины: нулевая или слишком малая экспонированная группа, редкие погодные явления и недостаточный размер выборок внутри отдельных видов ДТП.</p></section>
    </div>
  );
}
